<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { SvgIcon } from "@progress/kendo-vue-common";
import { ProgressBar } from "@progress/kendo-vue-progressbars";
import { userIcon, starIcon, starOutlineIcon } from '@progress/kendo-svg-icons'
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '@/logger.ts'
import { ref, onMounted } from 'vue'
import type { IAoeProductReview, IAoeProductReviewRatingSummary } from '@/types/ProductReviewTypes'
import { GetProductReviewsFromLocalStorageOrApi, ClearProductReviewCache } from '@/services/ProductService'
import { ProductReviewUserGet } from '@/api/ProductReview'
import {
  GetBeeNumberAuthenicatedUser,
  GetProductReviewUrl,
  GetReviewRootUrl
} from '@/stores/SettingsStore.ts'
import AoeProductReviewStars from './AoeProductReviewStars.vue'

const logger = useLogger()
setStoreLogger(logger)
logger.debug('AoeProductReviewList.vue')

const props = defineProps<{
  productcode: string
}>()

const reviewRootUrl = ref<string>('')
const beeNumberAuthenicatedUser = ref<number | null>(null)
const ratingSummary = ref<IAoeProductReviewRatingSummary | null>(null)
const reviews = ref<IAoeProductReview[] | null>([])
const userReview = ref<IAoeProductReview | null>(null)

onMounted(async () => {
  reviewRootUrl.value = await GetReviewRootUrl()
  beeNumberAuthenicatedUser.value = await GetBeeNumberAuthenicatedUser()

  // Check if user has any review (approved or pending) for this product
  if (beeNumberAuthenicatedUser.value) {
    try {
      const userReviewResp = await ProductReviewUserGet(props.productcode, beeNumberAuthenicatedUser.value)

      if (userReviewResp?.items && userReviewResp.items.length > 0) {
        // Verify the review actually belongs to the current user
        const matchingReview = userReviewResp.items.find(r => r.beeNumber === beeNumberAuthenicatedUser.value)
        if (matchingReview) {
          userReview.value = matchingReview

          // Clear cache to ensure fresh data when user has a review
          // This handles cases where review status changed (pending -> approved)
          ClearProductReviewCache(props.productcode)
        }
      }
    } catch (error) {
      logger.error('Error fetching user review:', error)
    }
  }

  GetProductReviewsFromLocalStorageOrApi(props.productcode).then((prodReviewResp) => {
    if (prodReviewResp == null) {
      return
    }

    ratingSummary.value = prodReviewResp?.ratingsSummary

    for (const pR of prodReviewResp?.items) {
      const date = new Date(pR.createdDate);
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }
      pR.createdDate = date.toLocaleString(navigator.language, options);
      reviews.value?.push(pR)
    }
  })
})

function addReviewClickedHandler() {
  logger.debug('addReviewClickedHandler')
  GetProductReviewUrl().then(url => {
    if (userReview.value) {
      // Use the approved reviews list version which includes attachments,
      // falling back to userReview if not found there
      const reviewWithAttachments = reviews.value?.find(
        r => r.aoeProductReviewId === userReview.value!.aoeProductReviewId
      ) ?? userReview.value

      // Edit existing review - store review data in localStorage
      localStorage.setItem('editReviewData', JSON.stringify(reviewWithAttachments))
      window.location.href = url + "?productCode=" + props.productcode +
                           "&reviewId=" + userReview.value.aoeProductReviewId +
                           "&mode=edit" +
                           "&returnURL=" + window.location.href
    } else {
      // Add new review
      window.location.href = url + "?productCode=" + props.productcode +
                           "&mode=add" +
                           "&returnURL=" + window.location.href
    }
  })
}

function productReviewImage(event: MouseEvent) {
  const target = event.target as HTMLImageElement
  target.classList.toggle('fullWidthImage')


  const overlay = document.getElementById("overlay")
  if (target.classList.contains('fullWidthImage')) {
    if (overlay != null) {
      overlay.style.display = "block"
    }
  } else {
    if (overlay != null) {
      overlay.style.display = "none"
    }
  }
}
</script>

<template>
  <div class="productReviewSection">
    <div class="row">
        <div class="col-12">
            <div class="review-header-container">
                <p class="pageHeader" v-if="beeNumberAuthenicatedUser || (ratingSummary?.total ?? 0) > 0">{{ t('productReview.ReviewHeader') }}</p>
                <div v-if="beeNumberAuthenicatedUser && !userReview" class="addRevewBtn">
                  <div class="btnReviewAction">
                    <button class="text-center btn primaryBtn" @click="addReviewClickedHandler">
                      {{ t('productReview.AddReviewButton') }}
                    </button>
                  </div>
                </div>
            </div>
        </div>
    </div>
    <div v-if="beeNumberAuthenicatedUser && userReview && !userReview.isApproved" class="row mt-2">
        <div class="col-12 text-end">
          <span class="review-pending-message">{{ t('productReview.ReviewPending') }}</span>
        </div>
    </div>
    <div v-if="(ratingSummary?.total ?? 0) > 0" class="productReviewSummary">
      <div class="row mt-2">
        <div class="col-12 col-md-2 mb-3 ratingsSummaryAverage">
          <div class="row">
            <div class="col-12 text-center ratingsAverage">
              {{ratingSummary?.average}}
            </div>
          </div>
          <div class="row">
            <div class="col-12 text-center ratingsSummaryTotalCount">
              {{ t('productReview.ReviewTotalPart1') }} {{ratingSummary?.total}} {{ t('productReview.ReviewTotalPart2') }}
            </div>
          </div>
          <div class="row">
            <div class="col-12 text-center">
              <AoeProductReviewStars :productcode="props.productcode" :showtotal="false" :usecolor="true" :starsize="'large'" />
            </div>
          </div>
        </div>
        <div class="col-12 col-md-10 ratingsSummaryAverageSliders">
          <div class="row">
            <div class="col-12 col-md-3 starSliderText">
              {{ t('productReview.Review5Star') }}
            </div>
            <div class="col-11 col-md-8 starSlider">
              <ProgressBar :max="ratingSummary?.total" :value="ratingSummary?.fiveStar" />
            </div>
            <div class="col-1 starCount">
              {{ratingSummary?.fiveStar}}
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-12 col-md-3 starSliderText">
              {{ t('productReview.Review4Star') }}
            </div>
            <div class="col-11 col-md-8 starSlider">
              <ProgressBar :max="ratingSummary?.total" :value="ratingSummary?.fourStar" />
            </div>
            <div class="col-1 starCount">
              {{ratingSummary?.fourStar}}
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-12 col-md-3 starSliderText">
              {{ t('productReview.Review3Star') }}
            </div>
            <div class="col-11 col-md-8 starSlider">
              <ProgressBar :max="ratingSummary?.total" :value="ratingSummary?.threeStar" />
            </div>
            <div class="col-1 starCount">
              {{ratingSummary?.threeStar}}
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-12 col-md-3 starSliderText">
              {{ t('productReview.Review2Star') }}
            </div>
            <div class="col-11 col-md-8 starSlider">
              <ProgressBar :max="ratingSummary?.total" :value="ratingSummary?.twoStar" />
            </div>
            <div class="col-1 starCount">
              {{ratingSummary?.twoStar}}
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-12 col-md-3 starSliderText">
              {{ t('productReview.Review1Star') }}
            </div>
            <div class="col-11 col-md-8 starSlider">
              <ProgressBar :max="ratingSummary?.total" :value="ratingSummary?.oneStar" />
            </div>
            <div class="col-1 starCount">
              {{ratingSummary?.oneStar}}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="productReviewDiv mt-3" v-for="prodRev in reviews?.values()" :key="prodRev.aoeProductReviewId">
      <div class="row productReviewItem">
          <div v-if="prodRev.firstName != undefined" class="row">
            <div class="col-2">
              <SvgIcon class="userImage" :icon="userIcon" />
            </div>
            <div class="col-7">
              <div>
                <p class="productReviewName">{{ prodRev.firstName }} {{ prodRev.lastName }}</p>
              </div>
              <div class="row">
                <div class="col-12">
                  <div class="row ratingsRow">
                    <div class="col-1" v-for="i in 5" :key="i">
                      <SvgIcon  v-if="i <= prodRev.rating" :icon="starIcon" :size="'large'" class="starColor"/>
                      <SvgIcon v-else  :icon="starOutlineIcon" :size="'large'" class="starColor"/>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-2 productReviewDateDiv">
              <p class="productReviewDate">{{prodRev.createdDate}}</p>
            </div>
          </div>
          <div v-if="prodRev.firstName == undefined" class="row">
            <div class="col-2">
              <SvgIcon class="userImage" :icon="userIcon" />
            </div>
            <div class="col-7">
              <div>
                <p class="productReviewName">{{ t('productReview.ReviewByAnonymous') }}</p>
              </div>
              <div class="row">
                <div class="col-12">
                  <div class="row ratingsRow">
                    <div class="col-1" v-for="i in 5" :key="i">
                      <SvgIcon  v-if="i <= prodRev.rating" :icon="starIcon" :size="'large'" class="starColor"/>
                      <SvgIcon v-else  :icon="starOutlineIcon" :size="'large'" class="starColor"/>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-2 productReviewDateDiv">
              <p class="productReviewDate">{{prodRev.createdDate}}</p>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <p class="productReviewItemComments">{{ prodRev.comments }}</p>
            </div>
          </div>
          <div class="row">
            <div class="col-12 reviewImageDiv">
              <div v-for="img in prodRev.attachments" :key="img.aoeProductReviewAttachmentId" class="reviewImage">
                <img v-if="img.isApproved && !img.filePath.includes('.mp4')" :src="reviewRootUrl + '/' + img.filePath" :alt="t('productReview.ReviewImageAlt')" class="rImage" @click="productReviewImage($event)" />
                <video v-if="img.isApproved && img.filePath.includes('.mp4')" :src="reviewRootUrl + '/' + img.filePath" class="rImage" controls @click="productReviewImage($event)"></video>
              </div>
            </div>
          </div>
          <div v-if="userReview && userReview.isApproved && prodRev.beeNumber === beeNumberAuthenicatedUser" class="row mt-2">
            <div class="col-12 text-end">
              <div class="btnReviewAction">
                <button class="text-center btn primaryBtn" @click="addReviewClickedHandler">
                  {{ t('productReview.EditReviewButton') }}
                </button>
              </div>
            </div>
          </div>
      </div>
    </div>
  </div>
  <div id="overlay" class="overLayImages">
    <div id="text" class="overLayText">{{ t('labels.ExitImageOverlay') }}</div>
  </div>
</template>

<style lang="scss">

@media screen and (max-width: 768px)
{
  .ratingsSummaryAverageSliders {
    margin-left: 0px;
    width: 69%;
  }
  .starSliderText {
    width: 100%;
  }
  .starSlider {
    width: 87%;
  }
}

@media screen and (min-width: 769px)
{
  .ratingsSummaryAverageSliders {
    margin-left: 10px;
    margin-right: -10px;
  }
}

.productReviewDateDiv {
  text-align: right;
  white-space: nowrap;
}

.review-header-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  gap: $spacing-2;
}

.review-header-container .pageHeader {
  margin: 0;
  flex: 1;
}

.addRevewBtn
{
  text-align: right;
  flex-shrink: 0;
}

@media screen and (max-width: 768px) {
  .review-header-container {
    flex-direction: column;
    align-items: flex-start;
    gap: $spacing-1;
  }

  .addRevewBtn {
    text-align: left;
    align-self: flex-end;
  }
}

.review-pending-message
{
  @include typography-small;
  @include font-weight-medium;
  color: #666;
  font-style: italic;
  display: inline-block;
}

.btnReviewAction {
  button.btn {
    font-weight: $font-weight-bold;
    border-radius: 10px;
    text-transform: uppercase !important;
    background-color: #477981;
    color: white;
    border: none;
    padding: 10px 20px;
    cursor: pointer;

    &:hover {
      background-color: #3a6269;
    }

    &:disabled {
      background-color: #cccccc;
      cursor: not-allowed;
    }
  }
}

.userImageDiv
{
  padding-top: 10px;
}

.userImage
{
  width: 60px;
  height: 60px;
}

.starColor {
  color: #ffaa45;
}

.ratingsRow
{
  width: 150px;
}

.productReviewName
{
  @include typography-body-large;
  @include font-weight-bold;
}

.productReviewDiv
{
  background-color: var(--vt-c-white-soft);
  border-radius: $radius-md;
  padding: $spacing-1;
  display: block;
}

.productReviewSummary
{
  padding: $spacing-1;
}

.reviewImageDiv
{
  -webkit-column-count: 4; /* Chrome, Safari, Opera */
    -moz-column-count: 4; /* Firefox */
    column-count: 4;
}

.reviewImage
{
  display: table-cell;
  align-content: flex-start;
  padding: $spacing-2-5;
}

.rImage
{
  width: 125px;
}

.starSlider .k-progressbar .k-progress-status
{
  display: none;
}

.starSlider .k-progressbar
{
  height: 5px;
  background-color: #7e7e7e
}

.starSlider .k-progressbar .k-selected {
  border-color: #ffaa45;
  background-color: #ffaa45;
}

.ratingsSummaryAverage
{
  background-color: var(--vt-c-white-soft);
  border-radius: $radius-md;
  padding: $spacing-1;
  display:inline-table;
}

.ratingsAverage
{
  @include typography-display;
  @include font-weight-bold;
  line-height: 50px;
  color: #2c7a87;
  padding: $spacing-2-5;
}

.ratingsSummaryTotalCount
{
  @include typography-body-large;
  color: #7e7e7e
}

.starCount
{
  @include typography-body;
  color: #7e7e7e
}

.starSliderText
{
  @include typography-h4;
  @include font-weight-regular;
  font-style: normal;
  line-height: 16px;
}

.ratingsSummaryStars
{
  display: inline;
  width: 20px;
  position: relative;
}

.fullWidthImage {
  width: 500px !important;
  position: fixed;
  top: 50%;
  left: 50%;
  padding: $card-padding;
  transform: translate(-50%, -50%);
  z-index: $z-kendo-popup-max;
}

.overLayImages {
  position: fixed; /* Sit on top of the page content */
  display: none; /* Hidden by default */
  width: 100%; /* Full width (cover the whole page) */
  height: 100%; /* Full height (cover the whole page) */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(black, 0.5); /* Black background with opacity */
  z-index: $z-kendo-popup-top; /* Specify a stack order in case you're using a different order for other elements */
  cursor: pointer; /* Add a pointer on hover */
}

.overLayText {
  position: absolute;
  top: 5%;
  @include typography-body-large;
  left: 50%;
  color: white;
  transform: translate(-50%,-50%);
  -ms-transform: translate(-50%,-50%);
  z-index: $z-kendo-popup-max;
}
</style>
